import { Drawer } from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import {
  ToggleButton,
  ToggleButtonGroup,
} from '@/components/ui/toggle-button-group';
import { Modal, ModalBody } from '@/components/ui/modal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  type FlashcardCountSnapshot,
  type FlashcardCountRequest,
  type FlashcardFilterRequest,
  fetchFlashcardCounts,
} from '../../../../services/flashcards';
import { useDispatch } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import { useStudyFlow } from '../../../../studyFlow';
import type { FlashcardsState } from '../../../../types/Flashcard';
import { enterStandalone } from '../../../../slices/studyContext/studyContextSlice';
import {
  applyTopicsUrlState,
  parseTopicsUrlState,
} from '../../../../utils/topicsUrlState';
import type { StudyReturnTo } from '../../../../slices/studyContext/studyContextSlice';
import useWindowWidth from '../../../../hooks/useWindowWidth';
import { capture } from '../../../../utils/analytics';
import { hapticsImpactLight } from '../../../../utils/haptics';
import {
  bootstrapFlashcardRunFromLegacyCards,
  bootstrapSessionFlashcardRun,
  bootstrapStandaloneFlashcardRun,
} from '../../../../services/flashcardRun';
import type {
  FlashcardRunOrder,
  FlashcardRunScope,
} from '../../../../slices/flashcardRun/flashcardRunSlice';
import Tooltip from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';

/** The scopes this modal can start: everything but a chat-served study set. */
type StartableFlashcardRunScope = Exclude<FlashcardRunScope, { kind: 'set' }>;

type ResolvedFlashcardCountSnapshot = {
  total: number;
  new: number;
  due: number;
  scheduled: number;
  suspended: number;
  linkedToday: number;
  linked: number;
};

const normalizeFlashcardCounts = (
  counts?: FlashcardCountSnapshot | null
): ResolvedFlashcardCountSnapshot => {
  const newCount = Math.max(0, counts?.new ?? 0);
  const dueCount = Math.max(0, counts?.due ?? 0);
  const scheduledCount = Math.max(0, counts?.scheduled ?? 0);
  const explicitSuspendedCount = Math.max(0, counts?.suspended ?? 0);

  const totalCount = Math.max(
    0,
    counts?.total ??
      newCount + dueCount + scheduledCount + explicitSuspendedCount
  );

  const derivedSuspendedCount = Math.max(
    0,
    totalCount - newCount - dueCount - scheduledCount
  );

  const suspendedCount =
    counts?.suspended !== undefined
      ? explicitSuspendedCount
      : derivedSuspendedCount;

  const linkedTodayCount = Math.max(0, counts?.linkedToday ?? 0);
  const linkedCount = Math.max(0, counts?.linked ?? 0);

  return {
    total: totalCount,
    new: newCount,
    due: dueCount,
    scheduled: scheduledCount,
    suspended: suspendedCount,
    linkedToday: linkedTodayCount,
    linked: linkedCount,
  };
};

interface StartFlashcardModalProps {
  open: boolean;
  onClose: () => void;
  examName: string;
  examId?: string;
  studySessionId?: string;
  topicIds?: string[];
  lectureIds?: string[];
  flashcardCounts?: FlashcardCountSnapshot;
  // Preselects the "count" amount with this value (clamped to what's
  // startable), e.g. an assignment's target count.
  defaultCount?: number;
  returnPath?: string | null;
  returnSearch?: string | null;
  returnTo?: StudyReturnTo;
  startEventName?: string;
  onFetchCounts?: (payload: FlashcardCountRequest) => Promise<
    | {
        data?: FlashcardCountSnapshot;
      }
    | FlashcardCountSnapshot
  >;
  onFetchSession?: (payload: FlashcardFilterRequest) => Promise<
    | {
        data?: FlashcardsState;
      }
    | FlashcardsState
  >;
  // Width below which the surface renders as a bottom drawer instead of a
  // centered modal. Defaults to 690px to match the QBank start surface;
  // lecture-preview flows raise it so the start surface matches the lecture
  // drawer's bottom-sheet band.
  drawerBreakpoint?: number;
}

const StartFlashcardModal = ({
  open,
  onClose,
  examName,
  examId,
  studySessionId,
  topicIds,
  lectureIds,
  flashcardCounts,
  defaultCount,
  returnPath,
  returnSearch,
  returnTo,
  startEventName = 'topics_flashcards_start',
  onFetchCounts,
  onFetchSession,
  drawerBreakpoint = 690,
}: StartFlashcardModalProps) => {
  const [flashcardAmount, setFlashcardAmount] = useState<string>('all');
  const [flashcardCount, setFlashcardCount] = useState<number>(1);
  const [flashcardCountInput, setFlashcardCountInput] = useState('1');
  const [flashcardType, setFlashcardType] = useState<string>('due');

  // Initial card ordering, only meaningful (and only shown) when studying both
  // new and due cards. Defaults to 'due' so the run matches historical behavior.
  const [flashcardOrder, setFlashcardOrder] =
    useState<FlashcardRunOrder>('due');

  const [isStarting, setIsStarting] = useState(false);
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);
  const [countsLoaded, setCountsLoaded] = useState(false);

  const [liveFlashcardCounts, setLiveFlashcardCounts] =
    useState<ResolvedFlashcardCountSnapshot | null>(null);

  const dispatch = useDispatch();
  const { sendPhaseEvent } = useStudyFlow();
  const didSetDefaultCountRef = useRef(false);
  const didSetDefaultTypeRef = useRef(false);
  const didSetDefaultAmountRef = useRef(false);

  const resolvedFlashcardCounts = useMemo(
    () => liveFlashcardCounts ?? normalizeFlashcardCounts(flashcardCounts),
    [flashcardCounts, liveFlashcardCounts]
  );

  const windowWidth = useWindowWidth();

  const showFlashcardCounts =
    liveFlashcardCounts != null || flashcardCounts != null;

  const resolvedNewCount = resolvedFlashcardCounts.new;
  const resolvedDueCount = resolvedFlashcardCounts.due;
  const resolvedLinkedCount = resolvedFlashcardCounts.linked;
  // The "Linked" filter is a today-session-only, opt-in option that appears when
  // the user has any outstanding NEW cards linked from missed QBank questions.
  // It studies all of them (regardless of when linked), bypassing the daily
  // new-card cap, so cards from a past block don't get stuck out of circulation.
  const showLinkedOption = Boolean(studySessionId) && resolvedLinkedCount > 0;

  const resolvedStartableCount =
    flashcardType === 'new'
      ? resolvedNewCount
      : flashcardType === 'due'
        ? resolvedDueCount
        : flashcardType === 'linked'
          ? resolvedLinkedCount
          : resolvedNewCount + resolvedDueCount;

  const maxFlashcardCount =
    resolvedStartableCount > 0 ? resolvedStartableCount : 50;

  const normalizedCount = useMemo(() => {
    if (!flashcardCount || flashcardCount < 1) return 1;
    return Math.min(flashcardCount, maxFlashcardCount);
  }, [flashcardCount, maxFlashcardCount]);

  const clampFlashcardCount = (numericValue: number) =>
    Math.max(1, Math.min(maxFlashcardCount, numericValue));

  const handleFlashcardCountChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const digitsOnly = event.target.value.replace(/\D+/g, '');
    const normalized = digitsOnly.replace(/^0+(?=\d)/, '');

    if (normalized === '') {
      setFlashcardCountInput('');
      return;
    }

    const numericValue = Number(normalized);
    if (Number.isNaN(numericValue)) {
      return;
    }

    const clampedCount = clampFlashcardCount(numericValue);
    setFlashcardCountInput(String(clampedCount));
    setFlashcardCount(clampedCount);
  };

  const handleFlashcardCountBlur = () => {
    const trimmed = flashcardCountInput.trim();
    const parsedValue = trimmed === '' ? null : Number(trimmed);

    const nextCount =
      parsedValue === null || Number.isNaN(parsedValue)
        ? 1
        : clampFlashcardCount(parsedValue);

    setFlashcardCount(nextCount);
    setFlashcardCountInput(String(nextCount));
  };

  const handleNumericKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
      'Tab',
      'Enter',
    ];

    if (allowedKeys.includes(event.key)) {
      if (event.key === 'Enter') {
        event.currentTarget.blur();
      }
      return;
    }

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  };

  const handleNumericPaste = (
    event: React.ClipboardEvent<HTMLInputElement>
  ) => {
    const text = event.clipboardData.getData('text');
    if (!/^\d+$/.test(text)) {
      event.preventDefault();
    }
  };

  // Render as a bottom drawer below the breakpoint, a centered modal at/above.
  const isDrawer = windowWidth < drawerBreakpoint;
  const controlsContext = isDrawer ? 'default' : 'modal';

  // At narrow widths the multi-option "New / Due / New & Due (/ Linked)"
  // toggle gets cramped, so collapse it into a select. Two bands: the upper
  // modal band (560–669px) and the narrow drawer band (below 435px). The modal
  // band only applies when we're actually rendering the modal — a full-width
  // lecture drawer at those widths has room for the toggle.
  const showFlashcardTypeAsSelect =
    windowWidth < 435 || (!isDrawer && windowWidth >= 560 && windowWidth < 670);

  // In the upper modal band the modal stays a row layout by default, which
  // crowds the amount toggle against the type select; stack them vertically
  // there. Only relevant while rendering the modal.
  const stackModalControls =
    !isDrawer && windowWidth >= 560 && windowWidth < 670;

  const flashcardTypeOptions = [
    { value: 'new', label: 'New' },
    { value: 'due', label: 'Due' },
    { value: 'both', label: 'New & Due' },
    ...(showLinkedOption ? [{ value: 'linked', label: 'Linked' }] : []),
  ];

  // Ordering only changes the presentation when a run mixes new and due cards,
  // so the control is shown exclusively for the "New & Due" type.
  const showOrderControl = flashcardType === 'both';

  const flashcardOrderOptions: Array<{
    value: FlashcardRunOrder;
    label: string;
    description: string;
  }> = [
    {
      value: 'due',
      label: 'Due First',
      description: 'Study due cards before new cards.',
    },
    {
      value: 'mixed',
      label: 'Mixed',
      description: 'Randomly shuffle due and new cards together.',
    },
    {
      value: 'new',
      label: 'New First',
      description: 'Study new cards before due cards.',
    },
  ];

  const topicIdList = useMemo(() => {
    if (!topicIds || topicIds.length === 0) return undefined;
    return Array.from(new Set(topicIds.filter(Boolean)));
  }, [topicIds]);

  const lectureIdList = useMemo(() => {
    if (!lectureIds || lectureIds.length === 0) return undefined;
    return Array.from(new Set(lectureIds.filter(Boolean)));
  }, [lectureIds]);

  // A study set is not one of the scopes this modal can build: it has no
  // filters to offer, and it is only ever launched from a chat block.
  const buildFlashcardScope = (): StartableFlashcardRunScope => {
    const amount = flashcardAmount === 'all' ? 'all' : 'count';
    const count = flashcardAmount === 'all' ? undefined : normalizedCount;
    // Only send an ordering for mixed-type runs; for single-type runs it has no
    // effect, so leave it unset and let the backend default to 'due'.
    const order = flashcardType === 'both' ? flashcardOrder : undefined;

    if (studySessionId) {
      return {
        kind: 'session',
        studySessionId,
        amount,
        count,
        // 'linked' is only selectable in the session context (gated by
        // showLinkedOption), so it is valid only on this branch.
        type: flashcardType as 'new' | 'due' | 'both' | 'linked',
        order,
      };
    }

    return {
      kind:
        lectureIdList && lectureIdList.length > 0 && !topicIdList?.length
          ? 'lecture'
          : 'filters',
      examId,
      topicIds: topicIdList,
      lectureIds: lectureIdList,
      amount,
      count,
      type: flashcardType as 'new' | 'due' | 'both',
      order,
    };
  };

  const buildFlashcardScopeKey = (scope: StartableFlashcardRunScope) => {
    if (scope.kind === 'session') {
      const countPart =
        scope.amount === 'count' ? String(scope.count ?? 0) : 'all';

      return `session:${scope.studySessionId}:${scope.type ?? 'both'}:${countPart}:${scope.order ?? 'due'}`;
    }

    const examPart = scope.examId ?? '';
    const topicPart = [...(scope.topicIds ?? [])].sort().join(',');
    const lecturePart = [...(scope.lectureIds ?? [])].sort().join(',');
    const countPart = scope.amount === 'all' ? 'all' : String(scope.count ?? 0);
    return `${scope.kind}:${examPart}:${topicPart}:${lecturePart}:${scope.type}:${countPart}:${scope.order ?? 'due'}`;
  };

  const buildFlashcardSearch = (scope: StartableFlashcardRunScope) => {
    const params = new URLSearchParams();
    if (scope.kind === 'session') {
      params.set('source', 'session');
      params.set('amount', scope.amount ?? 'all');
      params.set('type', scope.type ?? 'both');
      if (scope.order) {
        params.set('order', scope.order);
      }
      if (scope.amount === 'count' && scope.count) {
        params.set('count', String(scope.count));
      }
      return `?${params.toString()}`;
    }

    params.set('source', 'filters');
    params.set('kind', scope.kind);
    params.set('amount', scope.amount);
    params.set('type', scope.type);
    if (scope.order) {
      params.set('order', scope.order);
    }
    if (scope.examId) {
      params.set('examId', scope.examId);
    }
    if (scope.topicIds?.length) {
      params.set('topicIds', scope.topicIds.join(','));
    }
    if (scope.lectureIds?.length) {
      params.set('lectureIds', scope.lectureIds.join(','));
    }
    if (scope.amount === 'count' && scope.count) {
      params.set('count', String(scope.count));
    }

    return `?${params.toString()}`;
  };

  useEffect(() => {
    if (!open) return;
    if (didSetDefaultCountRef.current) return;
    let isMounted = true;

    const loadCounts = async () => {
      try {
        setIsLoadingCounts(true);

        const response = onFetchCounts
          ? await onFetchCounts({
              examId,
              topicIds: topicIdList,
              lectureIds: lectureIdList,
            })
          : await fetchFlashcardCounts({
              examId,
              topicIds: topicIdList,
              lectureIds: lectureIdList,
            });

        const payloadWithPossibleNestedData = response?.data ?? response;

        const payload =
          payloadWithPossibleNestedData &&
          typeof payloadWithPossibleNestedData === 'object' &&
          'data' in payloadWithPossibleNestedData
            ? (
                payloadWithPossibleNestedData as {
                  data?: FlashcardCountSnapshot;
                }
              ).data
            : payloadWithPossibleNestedData;

        const normalizedCounts = normalizeFlashcardCounts(payload);

        const initialCount = Math.min(
          Math.max(defaultCount ?? normalizedCounts.new, 1),
          maxFlashcardCount
        );

        if (isMounted) {
          setLiveFlashcardCounts(normalizedCounts);
          setFlashcardCount(initialCount);
          setFlashcardCountInput(String(initialCount));
          if (!didSetDefaultTypeRef.current) {
            setFlashcardType(normalizedCounts.due === 0 ? 'new' : 'due');
            didSetDefaultTypeRef.current = true;
          }
          setCountsLoaded(true);
          didSetDefaultCountRef.current = true;
        }
      } catch (error) {
        console.error('Failed to fetch flashcard counts:', error);
      } finally {
        if (isMounted) {
          setIsLoadingCounts(false);
          setCountsLoaded(true);
        }
      }
    };

    loadCounts();

    return () => {
      isMounted = false;
    };
  }, [
    examId,
    lectureIdList,
    maxFlashcardCount,
    onFetchCounts,
    open,
    topicIdList,
  ]);

  useEffect(() => {
    if (!open) {
      didSetDefaultCountRef.current = false;
      didSetDefaultTypeRef.current = false;
      didSetDefaultAmountRef.current = false;
      setCountsLoaded(false);
      setLiveFlashcardCounts(null);
      setFlashcardOrder('due');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setFlashcardAmount('all');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (didSetDefaultTypeRef.current) return;
    if (!showFlashcardCounts) return;
    const dueCount = resolvedFlashcardCounts.due;
    setFlashcardType(dueCount === 0 ? 'new' : 'due');
    didSetDefaultTypeRef.current = true;
  }, [open, resolvedFlashcardCounts.due, showFlashcardCounts]);

  // Safety net: if 'linked' is selected but the option becomes unavailable
  // (e.g. counts reload to zero linked cards), fall back to a valid type so the
  // control never sits on an unselectable value. A no-op in normal flows, where
  // 'linked' is only ever selected while the option is visible.
  useEffect(() => {
    if (!open) return;
    if (flashcardType === 'linked' && !showLinkedOption) {
      setFlashcardType(resolvedDueCount === 0 ? 'new' : 'due');
    }
  }, [open, flashcardType, showLinkedOption, resolvedDueCount]);

  useEffect(() => {
    if (!open) return;

    // With a caller-provided default (e.g. an assignment's target count), keep
    // the count pinned to it (clamped) instead of tracking the startable max.
    const nextCount =
      defaultCount != null
        ? Math.max(1, Math.min(maxFlashcardCount, defaultCount))
        : maxFlashcardCount;

    setFlashcardCount(nextCount);
    setFlashcardCountInput(String(nextCount));
  }, [defaultCount, flashcardType, maxFlashcardCount, open]);

  useEffect(() => {
    if (!open) return;
    if (didSetDefaultAmountRef.current) return;
    if (defaultCount == null) return;
    setFlashcardAmount('count');
    didSetDefaultAmountRef.current = true;
  }, [defaultCount, open]);

  const renderModalContent = () => {
    return (
      <ModalBody className="modal_content">
        <div className="start-modal-header">
          <h2>{examName}</h2>
          {showFlashcardCounts && (
            <div className="start-modal-badges" aria-label="Flashcard counts">
              <Tooltip
                title="Unseen flashcards."
                enterTouchDelay={0}
                enterDelay={250}
              >
                <span className="start-modal-badge">
                  <span className="start-modal-badge_value">
                    {resolvedFlashcardCounts.new}
                  </span>{' '}
                  New
                </span>
              </Tooltip>
              <Tooltip
                title="Previously studied flashcards due for review today."
                enterTouchDelay={0}
                enterDelay={250}
              >
                <span className="start-modal-badge">
                  <span className="start-modal-badge_value">
                    {resolvedFlashcardCounts.due}
                  </span>{' '}
                  Due
                </span>
              </Tooltip>
              <Tooltip
                title="Previously studied flashcards scheduled for future review."
                enterTouchDelay={0}
                enterDelay={250}
              >
                <span className="start-modal-badge">
                  <span className="start-modal-badge_value">
                    {resolvedFlashcardCounts.scheduled}
                  </span>{' '}
                  Scheduled
                </span>
              </Tooltip>
              <Tooltip
                title="Flashcards you've suspended from your deck."
                enterTouchDelay={0}
                enterDelay={250}
              >
                <span className="start-modal-badge">
                  <span className="start-modal-badge_value">
                    {resolvedFlashcardCounts.suspended}
                  </span>{' '}
                  Suspended
                </span>
              </Tooltip>
              {showLinkedOption && (
                <>
                  <Tooltip
                    title="Flashcards linked from QBank questions you missed. Studies all of them now, including ones linked on earlier days, so they don't get stuck out of circulation."
                    enterTouchDelay={0}
                    enterDelay={250}
                  >
                    <span className="start-modal-badge">
                      <span className="start-modal-badge_value">
                        {resolvedLinkedCount}
                      </span>{' '}
                      Linked
                    </span>
                  </Tooltip>
                </>
              )}
            </div>
          )}
        </div>
        <form
          className={[
            'start-flashcard-form',
            stackModalControls && 'start-flashcard-form--stacked',
            // When the order control is present (New & Due), let the form wrap
            // so the third toggle group drops to its own row instead of
            // overflowing the fit-content modal and being clipped.
            showOrderControl && 'start-flashcard-form--wrap',
          ]
            .filter(Boolean)
            .join(' ')}
          onSubmit={(event) => {
            event.preventDefault();
          }}
        >
          <ToggleButtonGroup
            exclusive
            className="flashcard-count-toggle-button-group"
            context={controlsContext}
            value={flashcardAmount}
            onChange={(_, newValue: string) => {
              if (newValue) setFlashcardAmount(newValue);
            }}
          >
            <ToggleButton value="all" sx={{ gap: '.375rem' }}>
              All
            </ToggleButton>
            <ToggleButton value="count" sx={{ gap: '.5rem' }}>
              <Input
                className="flashcard-count"
                value={flashcardCountInput}
                type="text"
                onChange={handleFlashcardCountChange}
                onBlur={handleFlashcardCountBlur}
                onKeyDown={handleNumericKeyDown}
                onPaste={handleNumericPaste}
                inputMode="numeric"
                pattern="[0-9]*"
                min={1}
                max={maxFlashcardCount}
                style={{ width: '4.75rem' }}
                context={controlsContext}
              />
            </ToggleButton>
          </ToggleButtonGroup>
          {showFlashcardTypeAsSelect ? (
            <Select
              value={flashcardType}
              onValueChange={(newValue) => {
                if (newValue) setFlashcardType(newValue);
              }}
            >
              <SelectTrigger
                className="flashcard-type-select"
                context={controlsContext}
                aria-label="Flashcard type"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {flashcardTypeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <ToggleButtonGroup
              exclusive
              className="flashcard-type-toggle-button-group"
              context={controlsContext}
              value={flashcardType}
              onChange={(_, newValue: string) => {
                if (newValue) setFlashcardType(newValue);
              }}
            >
              <ToggleButton value="new" sx={{ gap: '.5rem' }}>
                New
              </ToggleButton>
              <ToggleButton value="due" sx={{ gap: '.375rem' }}>
                Due
              </ToggleButton>
              <ToggleButton value="both" sx={{ gap: '.375rem' }}>
                New & Due
              </ToggleButton>
              {showLinkedOption && (
                <Tooltip
                  title="Flashcards linked from QBank questions you missed. Studies all of them now, including ones linked on earlier days, so they don't get stuck out of circulation."
                  enterTouchDelay={0}
                  enterDelay={250}
                >
                  <ToggleButton value="linked" sx={{ gap: '.375rem' }}>
                    Linked
                  </ToggleButton>
                </Tooltip>
              )}
            </ToggleButtonGroup>
          )}
          {showOrderControl &&
            (showFlashcardTypeAsSelect ? (
              <Select
                value={flashcardOrder}
                onValueChange={(newValue) => {
                  if (newValue)
                    setFlashcardOrder(newValue as FlashcardRunOrder);
                }}
              >
                <SelectTrigger
                  className="flashcard-order-select"
                  context={controlsContext}
                  aria-label="Flashcard order"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {flashcardOrderOptions.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      title={option.description}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <ToggleButtonGroup
                exclusive
                className="flashcard-order-toggle-button-group"
                context={controlsContext}
                value={flashcardOrder}
                aria-label="Flashcard order"
                onChange={(_, newValue: string) => {
                  if (newValue)
                    setFlashcardOrder(newValue as FlashcardRunOrder);
                }}
              >
                {flashcardOrderOptions.map((option) => (
                  <Tooltip
                    key={option.value}
                    title={option.description}
                    enterTouchDelay={0}
                    enterDelay={250}
                  >
                    <ToggleButton value={option.value} sx={{ gap: '.375rem' }}>
                      {option.label}
                    </ToggleButton>
                  </Tooltip>
                ))}
              </ToggleButtonGroup>
            ))}
        </form>
        <Button
          className="!w-full"
          context={controlsContext}
          disabled={isStarting || isLoadingCounts || !countsLoaded}
          onClick={async () => {
            if (isStarting) return;
            hapticsImpactLight();
            try {
              setIsStarting(true);
              const scope = buildFlashcardScope();

              let payload:
                | Awaited<ReturnType<typeof bootstrapStandaloneFlashcardRun>>
                | Awaited<ReturnType<typeof bootstrapSessionFlashcardRun>>
                | ReturnType<typeof bootstrapFlashcardRunFromLegacyCards>;

              // The legacy onFetchSession endpoint only supports new/due/both;
              // 'linked' is session-only and must go through the run endpoint
              // below. (No-op today: no caller pairs onFetchSession with a
              // studySessionId, so 'linked' is never selectable on this path.)
              if (onFetchSession && flashcardType !== 'linked') {
                const response = await onFetchSession({
                  examId,
                  topicIds: topicIdList,
                  lectureIds: lectureIdList,
                  amount: flashcardAmount === 'all' ? 'all' : 'count',
                  count:
                    flashcardAmount === 'all' ? undefined : normalizedCount,
                  type: flashcardType as 'new' | 'due' | 'both',
                });

                const legacyPayload =
                  response && 'data' in response ? response.data : response;

                payload = bootstrapFlashcardRunFromLegacyCards({
                  scope,
                  scopeKey: buildFlashcardScopeKey(scope),
                  flashcards: (legacyPayload ?? {
                    new: [],
                    due: [],
                    completed: [],
                  }) as FlashcardsState,
                });
              } else if (scope.kind === 'session') {
                payload = await bootstrapSessionFlashcardRun(
                  scope.studySessionId,
                  {
                    amount: scope.amount,
                    count: scope.count,
                    type: scope.type,
                    order: scope.order,
                  }
                );
              } else {
                payload = await bootstrapStandaloneFlashcardRun(scope);
              }

              const totalFlashcards =
                (payload?.priorityDueIds?.length ?? 0) +
                (payload?.newQueueIds?.length ?? 0) +
                (payload?.deferredDueIds?.length ?? 0);

              if (totalFlashcards === 0) {
                enqueueSnackbar('No flashcards found for this filter set.', {
                  autoHideDuration: 5000,
                });

                return;
              }
              if (returnTo && scope.kind !== 'session') {
                let resolvedReturnSearch = returnSearch ?? null;
                if (returnTo === 'topics') {
                  const nextTopicsState = returnSearch
                    ? parseTopicsUrlState(new URLSearchParams(returnSearch))
                    : { l1Id: topicIdList?.[0] };

                  const params = applyTopicsUrlState(
                    new URLSearchParams(),
                    nextTopicsState
                  );

                  resolvedReturnSearch = params.toString() || null;
                }

                dispatch(
                  enterStandalone({
                    blockId: 'flashcards',
                    returnTo,
                    returnPath,
                    returnSearch: resolvedReturnSearch,
                    flashcardScope: scope,
                  })
                );
              }
              onClose();

              sendPhaseEvent({
                type: 'START_FLASHCARDS',
                search: buildFlashcardSearch(scope),
              });

              capture(startEventName, {
                examName,
                lectureIds: lectureIdList,
                studySessionId:
                  scope.kind === 'session' ? scope.studySessionId : undefined,
                cardType: flashcardType,
                // The chosen order, present only for New & Due runs (undefined
                // otherwise), so adoption of the setting is measurable.
                cardOrder: scope.order,
              });
            } catch (error) {
              console.error('Failed to start flashcards:', error);

              enqueueSnackbar('Failed to start flashcards.', {
                variant: 'error',
              });
            } finally {
              setIsStarting(false);
            }
          }}
        >
          {isStarting
            ? 'Starting...'
            : isLoadingCounts
              ? 'Loading...'
              : 'Start Flashcards'}
        </Button>
      </ModalBody>
    );
  };

  return (
    <>
      {!isDrawer ? (
        <Modal
          open={open}
          onClose={() => {
            (document.activeElement as HTMLElement)?.blur();
            onClose();
          }}
          name="start-flashcards"
          className="modal--glass"
        >
          {renderModalContent()}
        </Modal>
      ) : (
        <Drawer
          className="modal--start-flashcards start-flashcard-drawer"
          aria-label="Start flashcard study"
          anchor="bottom"
          open={open}
          onClose={() => {
            (document.activeElement as HTMLElement)?.blur();
            onClose();
          }}
          onOpen={() => {}}
          swipeable
        >
          {renderModalContent()}
        </Drawer>
      )}
    </>
  );
};

export default StartFlashcardModal;
